import React, { useRef, useState, useEffect } from 'react';
import { useImageEditor } from './hooks/useImageEditor';
import Toolbar from './components/Toolbar';
import CanvasArea from './components/CanvasArea';
import CropPanel from './components/CropPanel';
import ResizePanel from './components/ResizePanel';
import FlipPanel from './components/FlipPanel';
import AdjustmentsPanel from './components/AdjustmentsPanel';
import ColorBalancePanel from './components/ColorBalancePanel';
import CurvesPanel from './components/CurvesPanel';
import SkinEnhancePanel from './components/SkinEnhancePanel';
import VariantPicker from './components/VariantPicker';
import RedEyePanel from './components/RedEyePanel';
import BlemishPanel from './components/BlemishPanel';
import { PenToolPanel } from './components/PenToolOverlay';

/**
 * Map toolbar tool IDs to their corresponding panel components.
 */
const TOOL_PANELS = {
  crop: CropPanel,
  resize: ResizePanel,
  flip: FlipPanel,
  penTool: PenToolPanel,
  skinEnhance: SkinEnhancePanel,
  skinEnhanceVariants: VariantPicker,
  colorBalance: ColorBalancePanel,
  curves: CurvesPanel,
  brightnessContrast: AdjustmentsPanel,
  sharpen: AdjustmentsPanel,
  vignette: AdjustmentsPanel,
  redEye: RedEyePanel,
  blemish: BlemishPanel,
};

/**
 * ImageEditorPage — main layout. No top header bar.
 * Upload/Undo/Redo/Revert are in the left toolbar.
 *
 * +----------+------------------+-------------+
 * | Toolbar  |   Canvas Area    | Tool Panel  |
 * | (left)   |   (center)       | (right)     |
 * +----------+------------------+-------------+
 */
export default function ImageEditorPage() {
  const fileInputRef = useRef(null);
  const editor = useImageEditor();
  const [toast, setToast] = useState(null);

  // Show error as toast
  useEffect(() => {
    if (editor.error) {
      setToast(editor.error);
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [editor.error]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) editor.loadImage(file);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSelectTool = (toolId) => {
    if (toolId === 'save') {
      editor.save({ filename: 'edited-image.png' });
      return;
    }
    if (toolId === 'download') {
      editor.download();
      return;
    }
    if (toolId === 'backgroundRemove') {
      editor.backgroundRemove();
      return;
    }
    editor.setActiveTool(toolId);
  };

  const ActivePanel = editor.activeTool ? TOOL_PANELS[editor.activeTool] : null;

  return (
    <div style={{
      display: 'flex', height: '100vh', width: '100%',
      background: '#0f0f1a', color: '#eee', fontFamily: 'sans-serif',
      overflow: 'hidden',
    }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Left toolbar */}
      <Toolbar
        activeTool={editor.activeTool}
        onSelectTool={handleSelectTool}
        onUpload={handleUploadClick}
        onUndo={editor.undo}
        onRedo={editor.redo}
        onRevert={editor.revertToOriginal}
        canUndo={editor.undoStack.length > 0}
        canRedo={editor.redoStack.length > 0}
        hasOriginal={!!editor.originalImage && editor.originalImage !== editor.currentImage}
      />

      {/* Canvas */}
      <CanvasArea
        imageSrc={editor.currentImage}
        loading={editor.loading}
        onUpload={handleUploadClick}
      />

      {/* Right panel */}
      {ActivePanel && (
        <div style={{
          boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
          borderRadius: '8px 0 0 8px',
          overflow: 'hidden',
        }}>
          <ActivePanel editor={editor} />
        </div>
      )}

      {/* Toast error messages */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#e74c3c', color: '#fff', padding: '10px 24px',
          borderRadius: 8, fontSize: 13, zIndex: 1000,
          boxShadow: '0 4px 20px rgba(231,76,60,0.4)',
          animation: 'fadeIn 0.3s ease',
        }}>
          {toast}
          <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
        </div>
      )}
    </div>
  );
}
